import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, CheckCircle2, ShieldCheck, Sparkles, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { navigateWithTransition } from "@/lib/viewTransition";
import { AuthLayout } from "@/components/AuthLayout";
import { Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Button } from "@/components/ui/Button";

const FEATURES = [
  { icon: Building2, label: "Un panel para gestionar todo tu club" },
  { icon: Users2, label: "Equipo dedicado para acompañaros en la puesta en marcha" },
  { icon: ShieldCheck, label: "Revisamos cada solicitud personalmente, sin bots" },
];

const schema = z
  .object({
    companyName: z.string().min(2, "Mínimo 2 caracteres"),
    contactName: z.string().min(2, "Mínimo 2 caracteres"),
    email: z.string().email("Email inválido"),
    phone: z.string().min(6, "Teléfono inválido"),
    password: z.string().min(8, "Mínimo 8 caracteres"),
    confirmPassword: z.string(),
    city: z.string().optional(),
    message: z.string().optional(),
    clubCount: z.coerce.number().int().min(1, "Mínimo 1").max(10, "Para más de 10 clubes, contáctanos directamente"),
    additionalClubs: z.array(z.object({ name: z.string().min(2, "Mínimo 2 caracteres") })).optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });
type FormValues = z.infer<typeof schema>;

function LoginLink() {
  const navigate = useNavigate();
  return (
    <a
      href="/login"
      onClick={(e) => {
        e.preventDefault();
        navigateWithTransition(navigate, "/login");
      }}
      className="font-medium text-brand-600 hover:text-brand-700"
    >
      Inicia sesión
    </a>
  );
}

export function RegisterProviderPage() {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof schema>, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { clubCount: 1, additionalClubs: [] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "additionalClubs" });
  const clubCount = watch("clubCount");

  useEffect(() => {
    const target = Math.max(0, (Number(clubCount) || 1) - 1);
    if (fields.length < target) {
      for (let i = fields.length; i < target; i++) append({ name: "" });
    } else if (fields.length > target) {
      for (let i = fields.length - 1; i >= target; i--) remove(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubCount]);

  async function onSubmit(data: FormValues) {
    setSubmitError(null);
    const { confirmPassword, clubCount: _clubCount, additionalClubs, ...rest } = data;
    const payload = { ...rest, additionalClubNames: (additionalClubs ?? []).map((c) => c.name) };
    try {
      await api.post("/registrations", payload);
      setSent(true);
    } catch (error: any) {
      setSubmitError(error?.response?.data?.error ?? "No se pudo enviar la solicitud. Inténtalo de nuevo.");
    }
  }

  return (
    <AuthLayout
      headline="¿Tenéis un club de pádel? Unámonos."
      subtext="Cuéntanos sobre tu club y nuestro equipo se pondrá en contacto contigo para estudiar cómo podemos ayudaros a gestionar reservas, marcadores y rankings."
      features={FEATURES}
      formEyebrow={
        !sent && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
            <Sparkles className="h-3.5 w-3.5" />
            Registro de nuevos clubes
          </span>
        )
      }
    >
      {sent ? (
        <div className="flex flex-col items-start gap-4">
          <span className="auth-in flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <h2 className="auth-in text-2xl font-bold text-ink-900" style={{ animationDelay: "60ms" }}>
            ¡Solicitud enviada!
          </h2>
          <p className="auth-in text-sm text-ink-500" style={{ animationDelay: "120ms" }}>
            Gracias por vuestro interés. Nuestro equipo revisará la solicitud y se pondrá en
            contacto con vosotros lo antes posible.
          </p>
          <div className="auth-in" style={{ animationDelay: "180ms" }}>
            <a
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                navigateWithTransition(navigate, "/login");
              }}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Volver al inicio de sesión
            </a>
          </div>
        </div>
      ) : (
        <>
          <h2 className="auth-in text-2xl font-bold text-ink-900">Cuéntanos sobre tu club</h2>
          <p className="auth-in mt-1 mb-6 text-sm text-ink-400" style={{ animationDelay: "60ms" }}>
            Los registros se validan manualmente por nuestro equipo — no se crea ninguna
            cuenta automáticamente. La contraseña que definas aquí será la que uses para
            iniciar sesión una vez que aprobemos vuestra solicitud.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="auth-in flex flex-col gap-1.5" style={{ animationDelay: "110ms" }}>
              <label htmlFor="clubCount" className="text-sm font-medium text-ink-600">
                ¿Cuántos clubes tienes a tu cargo?
              </label>
              <select
                id="clubCount"
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                {...register("clubCount")}
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              {errors.clubCount && <p className="text-xs text-red-600">{errors.clubCount.message}</p>}
            </div>

            {Number(clubCount) > 1 ? (
              <div className="auth-in flex flex-col gap-3 rounded-xl bg-ink-50/60 p-4" style={{ animationDelay: "130ms" }}>
                <p className="text-sm font-medium text-ink-600">Nombre de cada club ({Number(clubCount)} en total)</p>
                <Input id="companyName" label="Club 1" error={errors.companyName?.message} {...register("companyName")} />
                {fields.map((field, index) => (
                  <Input
                    key={field.id}
                    id={`additionalClubs.${index}.name`}
                    label={`Club ${index + 2}`}
                    error={errors.additionalClubs?.[index]?.name?.message}
                    {...register(`additionalClubs.${index}.name` as const)}
                  />
                ))}
              </div>
            ) : (
              <div className="auth-in" style={{ animationDelay: "130ms" }}>
                <Input id="companyName" label="Nombre del club/empresa" error={errors.companyName?.message} {...register("companyName")} />
              </div>
            )}
            <div className="auth-in" style={{ animationDelay: "150ms" }}>
              <Input id="contactName" label="Persona de contacto" error={errors.contactName?.message} {...register("contactName")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "190ms" }}>
              <Input id="email" type="email" label="Email" error={errors.email?.message} {...register("email")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "230ms" }}>
              <Input id="phone" label="Teléfono" error={errors.phone?.message} {...register("phone")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "270ms" }}>
              <PasswordInput id="password" label="Contraseña" error={errors.password?.message} {...register("password")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "310ms" }}>
              <PasswordInput
                id="confirmPassword"
                label="Confirmar contraseña"
                error={errors.confirmPassword?.message}
                {...register("confirmPassword")}
              />
            </div>
            <div className="auth-in" style={{ animationDelay: "350ms" }}>
              <Input id="city" label="Ciudad (opcional)" {...register("city")} />
            </div>
            <div className="auth-in flex flex-col gap-1.5" style={{ animationDelay: "390ms" }}>
              <label htmlFor="message" className="text-sm font-medium text-ink-600">
                Cuéntanos más (opcional)
              </label>
              <textarea
                id="message"
                rows={3}
                className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                {...register("message")}
              />
            </div>

            {submitError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{submitError}</p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="auth-in mt-2 w-full transition-[transform,box-shadow,filter] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:brightness-105 active:translate-y-0 active:brightness-95"
              style={{ animationDelay: "430ms" }}
            >
              {isSubmitting ? "Enviando…" : "Enviar solicitud"}
            </Button>
          </form>

          <p className="auth-in mt-6 text-center text-sm text-ink-400" style={{ animationDelay: "470ms" }}>
            ¿Ya tenéis acceso? <LoginLink />
          </p>
        </>
      )}
    </AuthLayout>
  );
}
